'use client';

import ReactECharts from 'echarts-for-react';
import { useMemo, useState, useEffect } from 'react';
import { useTheme } from 'next-themes';
import { ParsedFitResult } from '@/lib/fitParser';
import { Flame, Zap, Battery, Activity } from 'lucide-react';
import * as echarts from 'echarts';

const DURATIONS = [1, 5, 15, 30, 60, 120, 300, 600, 1200, 3600];
const DUR_LABELS = ['1s', '5s', '15s', '30s', '1m', '2m', '5m', '10m', '20m', '1h'];

export function PowerProfileView({ realData }: { realData?: ParsedFitResult[] | null }) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  
  const isDark = mounted ? resolvedTheme === 'dark' : true;
  const textColor = isDark ? '#94a3b8' : '#6b7280';
  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  const { maxPowers, cp, wPrime } = useMemo(() => {
    if (!realData) return { maxPowers: DURATIONS.map(() => 0), cp: 0, wPrime: 0 };
    
    const validRides = realData.filter(d => 
      (d.sport.toLowerCase().includes('cycl') || d.sport.toLowerCase().includes('bik'))
      && d.telemetry?.power?.length > 0
    );
    
    const bests = DURATIONS.map(() => 0);
    
    validRides.forEach(r => {
      const pwr = r.telemetry.power;
      for (let i = 0; i < DURATIONS.length; i++) {
        const d = DURATIONS[i];
        if (pwr.length < d) continue;
        
        let sum = 0;
        for (let j = 0; j < d; j++) sum += pwr[j];
        let maxAvg = sum / d;
        
        for (let j = d; j < pwr.length; j++) {
          sum += pwr[j] - pwr[j - d];
          const avg = sum / d;
          if (avg > maxAvg) maxAvg = avg;
        }
        
        if (maxAvg > bests[i]) bests[i] = maxAvg;
      }
    });

    const p5 = bests[6] || 0; // 300s
    const p20 = bests[8] || 0; // 1200s
    
    let calcCp = 0;
    let calcWp = 0;
    if (p5 > 0 && p20 > 0 && p5 > p20) {
      const w5 = p5 * 300;
      const w20 = p20 * 1200;
      calcCp = (w20 - w5) / (1200 - 300);
      calcWp = w5 - (calcCp * 300);
    }
    
    return { maxPowers: bests, cp: calcCp, wPrime: calcWp };
  }, [realData]);

  const ftp = Math.max(1, maxPowers[8] || 1); // 20m power
  const nm = maxPowers[1] || 0;  // 5s
  const ac = maxPowers[4] || 0;  // 1m
  const map = maxPowers[6] || 0; // 5m

  const nmRatio = nm / ftp;
  const acRatio = ac / ftp;
  const mapRatio = map / ftp;

  let phenotype = 'Rouleur';
  let phenotypeDesc = 'Balanced across all durations. A versatile rider with no distinct weaknesses.';
  let PhenotypeIcon = Activity;
  let phenoColor = 'text-amber-500';
  let phenoBg = 'bg-amber-500/20';
  let phenoBorder = 'border-amber-500/20';
  let phenoGrad = 'from-amber-500/10 to-yellow-500/10';
  let phenoColorHex = '#f59e0b';
  let gradStops = [
    { offset: 0, color: 'rgba(245, 158, 11, 0.7)' },
    { offset: 1, color: 'rgba(234, 179, 8, 0.2)' }
  ];

  if (nmRatio > 5.5 && acRatio > 2.5) {
    phenotype = 'Sprinter';
    phenotypeDesc = 'Exceptional top-end power. You can produce massive wattage for short durations and explosive finishes.';
    PhenotypeIcon = Zap;
    phenoColor = 'text-violet-500';
    phenoBg = 'bg-violet-500/20';
    phenoBorder = 'border-violet-500/20';
    phenoGrad = 'from-violet-500/10 to-fuchsia-500/10';
    phenoColorHex = '#8b5cf6';
    gradStops = [
      { offset: 0, color: 'rgba(139, 92, 246, 0.7)' },
      { offset: 1, color: 'rgba(217, 70, 239, 0.2)' }
    ];
  } else if (acRatio > 2.5 && mapRatio > 1.25) {
    phenotype = 'Attacker';
    phenotypeDesc = 'Strong 1-to-5 minute power, perfect for breakaways, puncheur finishes, and short sharp climbs.';
    PhenotypeIcon = Flame;
    phenoColor = 'text-rose-500';
    phenoBg = 'bg-rose-500/20';
    phenoBorder = 'border-rose-500/20';
    phenoGrad = 'from-rose-500/10 to-red-500/10';
    phenoColorHex = '#f43f5e';
    gradStops = [
      { offset: 0, color: 'rgba(244, 63, 94, 0.7)' },
      { offset: 1, color: 'rgba(239, 68, 68, 0.2)' }
    ];
  } else if (mapRatio > 1.25 && nmRatio < 5.0) {
    phenotype = 'Pursuiter';
    phenotypeDesc = 'Incredible 5-minute power. Built for sustained high-intensity efforts and track-style pursuits.';
    PhenotypeIcon = Battery;
    phenoColor = 'text-blue-500';
    phenoBg = 'bg-blue-500/20';
    phenoBorder = 'border-blue-500/20';
    phenoGrad = 'from-blue-500/10 to-cyan-500/10';
    phenoColorHex = '#3b82f6';
    gradStops = [
      { offset: 0, color: 'rgba(59, 130, 246, 0.7)' },
      { offset: 1, color: 'rgba(6, 182, 212, 0.2)' }
    ];
  } else if (nmRatio < 4.0 && acRatio < 2.0 && mapRatio < 1.15) {
    phenotype = 'Time Trialist';
    phenotypeDesc = 'Very flat power curve. You excel at long, steady, threshold efforts and sustained climbs.';
    PhenotypeIcon = Activity;
    phenoColor = 'text-emerald-500';
    phenoBg = 'bg-emerald-500/20';
    phenoBorder = 'border-emerald-500/20';
    phenoGrad = 'from-emerald-500/10 to-green-500/10';
    phenoColorHex = '#10b981';
    gradStops = [
      { offset: 0, color: 'rgba(16, 185, 129, 0.7)' },
      { offset: 1, color: 'rgba(34, 197, 94, 0.2)' }
    ];
  }


  const maxNm = Math.max(nm * 1.15, ftp * 4.5 / 0.75);
  const maxAc = Math.max(ac * 1.15, ftp * 2.2 / 0.75);
  const maxMap = Math.max(map * 1.15, ftp * 1.2 / 0.75);
  const maxFtp = Math.max(ftp * 1.15, ftp / 0.75);

  const radarOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      backgroundColor: isDark ? '#1c1c1e' : '#fff',
      borderColor: 'transparent',
      textStyle: { color: isDark ? '#f0f0f0' : '#1a1a1a', fontSize: 11 },
    },
    radar: {
      radius: '65%',
      center: ['50%', '55%'],
      indicator: [
        { name: 'NM (5s)', max: maxNm, min: 0, color: '#8b5cf6' },
        { name: 'AC (1m)', max: maxAc, min: 0, color: '#f43f5e' },
        { name: 'MAP (5m)', max: maxMap, min: 0, color: '#3b82f6' },
        { name: 'FTP (20m)', max: maxFtp, min: 0, color: '#10b981' }
      ],
      splitNumber: 4,
      axisName: { color: textColor, fontSize: 11, fontWeight: 600 },
      splitArea: {
        areaStyle: {
          color: isDark ? ['rgba(255,255,255,0.02)', 'rgba(255,255,255,0.04)'] : ['rgba(0,0,0,0.02)', 'rgba(0,0,0,0.04)']
        }
      },
      axisLine: { lineStyle: { color: gridColor } },
      splitLine: { lineStyle: { color: gridColor } }
    },
    series: [{
      type: 'radar',
      symbol: 'none',
      data: [{
        value: [nm, ac, map, ftp],
        name: '4DP Profile',
        areaStyle: { 
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 1, y2: 1,
            colorStops: gradStops
          }
        },
        lineStyle: { 
          width: 3, 
          color: phenoColorHex
        }
      }]
    }]
  };

  const option = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: isDark ? '#1c1c1e' : '#fff',
      borderColor: 'transparent',
      textStyle: { color: isDark ? '#f0f0f0' : '#1a1a1a', fontSize: 11 },
      formatter: (params: any) => {
        return `<strong>${params[0].name}</strong><br/>${Math.round(params[0].value)} Watts`;
      }
    },
    grid: { top: 40, right: 10, bottom: 30, left: 40 },
    xAxis: {
      type: 'category',
      data: DUR_LABELS,
      axisLine: { lineStyle: { color: gridColor } },
      axisTick: { show: false },
      axisLabel: { color: textColor, fontSize: 10 },
    },
    yAxis: {
      type: 'value',
      name: 'Watts',
      nameTextStyle: { color: textColor, fontSize: 10, align: 'right' },
      splitLine: { lineStyle: { color: gridColor } },
      axisLabel: { color: textColor, fontSize: 10 },
    },
    series: [
      {
        data: maxPowers.map(p => Math.round(p)),
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: { color: '#f59e0b' },
        lineStyle: { width: 3, color: '#f59e0b' },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(245, 158, 11, 0.4)' },
            { offset: 1, color: 'rgba(245, 158, 11, 0)' }
          ])
        }
      }
    ]
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Radar Chart */}
        <div className="rounded-2xl bg-card border border-border/50 shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)] p-5">
           <h3 className="text-sm font-semibold tracking-tight mb-4">4D Rider Profile</h3>
           <div className="h-[280px] w-full">
             <ReactECharts option={radarOption} style={{ height: '100%', width: '100%' }} />
           </div>
        </div>
        
        {/* Profile Details & KPIs */}
        <div className="flex flex-col gap-4">
           {/* Phenotype Card */}
           <div className={`flex-1 flex flex-col justify-center p-6 rounded-2xl bg-gradient-to-br ${phenoGrad} border ${phenoBorder}`}>
              <div className="flex items-center gap-3 mb-3">
                 <div className={`p-3 rounded-xl ${phenoBg} ${phenoColor}`}>
                   <PhenotypeIcon className="w-6 h-6" />
                 </div>
                 <h2 className={`text-2xl font-bold tracking-tight ${phenoColor}`}>{phenotype}</h2>
              </div>
              <p className="text-sm text-foreground/80 leading-relaxed mb-4">{phenotypeDesc}</p>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-auto pt-4 border-t border-foreground/10 text-center">
                 <div>
                    <span className="block text-[9px] font-bold text-violet-500 uppercase mb-0.5">Sprint (5s)</span>
                    <span className="text-sm font-semibold">{nmRatio.toFixed(1)}x <span className="text-[10px] font-normal text-muted-foreground">FTP</span></span>
                 </div>
                 <div>
                    <span className="block text-[9px] font-bold text-rose-500 uppercase mb-0.5">Anaerobic</span>
                    <span className="text-sm font-semibold">{acRatio.toFixed(1)}x <span className="text-[10px] font-normal text-muted-foreground">FTP</span></span>
                 </div>
                 <div>
                    <span className="block text-[9px] font-bold text-blue-500 uppercase mb-0.5">MAP (5m)</span>
                    <span className="text-sm font-semibold">{mapRatio.toFixed(1)}x <span className="text-[10px] font-normal text-muted-foreground">FTP</span></span>
                 </div>
                 <div>
                    <span className="block text-[9px] font-bold text-emerald-500 uppercase mb-0.5">Threshold</span>
                    <span className="text-sm font-semibold">1.0x <span className="text-[10px] font-normal text-muted-foreground">FTP</span></span>
                 </div>
              </div>
           </div>

           {/* Mini KPIs */}
           <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <div className="p-4 rounded-xl bg-card border border-border/50 shadow-[0_2px_12px_rgba(0,0,0,0.04)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.2)] text-center">
                 <span className="block text-[10px] uppercase font-bold text-muted-foreground mb-1">Critical Power</span>
                 <span className="text-xl font-bold text-amber-500">{Math.round(cp)} <span className="text-sm text-muted-foreground">W</span></span>
              </div>
              <div className="p-4 rounded-xl bg-card border border-border/50 shadow-[0_2px_12px_rgba(0,0,0,0.04)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.2)] text-center">
                 <span className="block text-[10px] uppercase font-bold text-muted-foreground mb-1">W-Prime</span>
                 <span className="text-xl font-bold text-rose-500">{Math.round(wPrime/1000)} <span className="text-sm text-muted-foreground">kJ</span></span>
              </div>
              <div className="p-4 rounded-xl bg-card border border-border/50 shadow-[0_2px_12px_rgba(0,0,0,0.04)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.2)] text-center">
                 <span className="block text-[10px] uppercase font-bold text-muted-foreground mb-1">Pmax (1s)</span>
                 <span className="text-xl font-bold text-sky-500">{Math.round(maxPowers[0])} <span className="text-sm text-muted-foreground">W</span></span>
              </div>
           </div>
        </div>
      </div>

      <div className="rounded-2xl bg-card border border-border/50 shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)] p-5">
        <h3 className="text-sm font-semibold tracking-tight mb-4">Mean Maximal Power Curve</h3>
        <div className="h-[400px] w-full">
          <ReactECharts option={option} style={{ height: '100%', width: '100%' }} />
        </div>
      </div>
    </div>
  );
}
